import { useState, ChangeEvent, useEffect } from 'react';
import { TeButton, TeContainerSlim, TeIcon, TeText } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '@topcon/topcon-utils';

import UploadButton from './UploadButton';
import FileDropContainer from './FileDropContainer';

interface UploadAreaProps {
  acceptedFiles: string[];
  onUploadFile: (file: IUploadFile | null) => void;
  maxFileSize: number;
  value?: IUploadFile | null;
  clearable?: boolean;
  disabled?: boolean;
}

export interface IUploadFile {
  url: string;
  name: string;
}

const UploadFileArea = ({
  acceptedFiles,
  onUploadFile,
  maxFileSize,
  value,
  clearable = true,
  disabled = false,
}: UploadAreaProps) => {
  const [fileBeingDragged, setFileBeingDragged] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [selectedName, setSelectedName] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const notificationService = useNotification();

  // sync external controlled value into local preview/state
  useEffect(() => {
    if (value && value.url) {
      setPreviewUrl(value.url);
      setSelectedName(value.name);
    } else {
      setPreviewUrl(undefined);
      setSelectedName(undefined);
    }
  }, [value]);

  const handleClear = () => {
    setPreviewUrl(undefined);
    setSelectedName(undefined);
    onUploadFile(null);
  };

  const dragStart = () => setFileBeingDragged(true);

  const dragEnd = () => setFileBeingDragged(false);

  const fileDrop = (files: File[]) => {
    dragEnd();
    if (files.length > 1) {
      notificationService.addNotification(
        t('notifications.multipleFiles'),
        t('notifications.multipleFilesDescription'),
        'error'
      );
      return;
    }
    const [file] = files;
    if (!file) return;
    handleUploadFile(file);
  };

  const handleButtonFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    if (files.length > 1) {
      notificationService.addNotification(
        t('notifications.multipleFiles'),
        t('notifications.multipleFilesDescription'),
        'error'
      );
      return;
    }

    const file = files[0];
    if (file) {
      handleUploadFile(file);
    }

    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  const handleUploadFile = (file: File) => {
    // Validate file is a proper File instance
    if (!file || !(file instanceof File)) {
      notificationService.addNotification(
        t('notifications.invalidFile'),
        t('notifications.invalidFileDescription'),
        'error'
      );
      return;
    }

    // Validate file exists and has content
    if (file.size === 0) {
      notificationService.addNotification(
        t('notifications.invalidFile'),
        t('notifications.invalidFileDescription'),
        'error'
      );
      return;
    }

    // Max file size check
    if (maxFileSize && file.size > maxFileSize) {
      notificationService.addNotification(
        t('notifications.fileTooLarge', { size: (file.size / (1024 * 1024)).toFixed(2) }),
        t('notifications.fileTooLargeDescription'),
        'error'
      );
      return;
    }

    // Validate file type - check MIME type first, fallback to extension if MIME type is empty
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const isValidMimeType = file.type && acceptedFiles.includes(file.type);
    const isValidExtension =
      fileExtension &&
      (((fileExtension === 'jpg' || fileExtension === 'jpeg') &&
        acceptedFiles.includes('image/jpeg')) ||
        (fileExtension === 'png' && acceptedFiles.includes('image/png')));

    if (!isValidMimeType && !isValidExtension) {
      notificationService.addNotification(
        t('notifications.invalidFileType', { fileType: file.type || fileExtension || 'unknown' }),
        t('notifications.invalidFileTypeDescription'),
        'error'
      );
      return;
    }

    // Read the file as Data URL (base64 encoded) to pass to the modal and to show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event?.target?.result?.toString();
      if (dataUrl) {
        // set preview + name locally for immediate UI feedback
        setPreviewUrl(dataUrl);
        setSelectedName(file.name);
        // notify parent with dataUrl + filename
        onUploadFile({ url: dataUrl, name: file.name });
      } else {
        notificationService.addNotification(
          t('notifications.invalidFile'),
          t('notifications.invalidFileDescription'),
          'error'
        );
      }
    };
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      notificationService.addNotification(
        t('notifications.fileReadError'),
        t('notifications.fileReadErrorDescription'),
        'error'
      );
    };
    reader.onabort = () => {
      notificationService.addNotification(
        t('notifications.fileReadError'),
        t('notifications.fileReadErrorDescription'),
        'error'
      );
    };

    try {
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error reading file:', error);
      notificationService.addNotification(
        t('notifications.fileReadError'),
        t('notifications.fileReadErrorDescription'),
        'error'
      );
    }
  };

  const isPreviewImage = (url?: string, name?: string) => {
    if (!url && !name) return false;
    if (url && url.startsWith('data:image/')) return true;
    const ext = (name || '').split('.').pop() || '';
    return /^(png|jpe?g|gif|webp|svg)$/i.test(ext);
  };

  const allowedFileExtensions = acceptedFiles
    .map((type) => {
      if (type.startsWith('image/')) {
        return type.split('/')[1];
      }
      return type;
    })
    .map((ext) => ext.toLocaleUpperCase())
    .filter((ext) => ext && !ext.includes('/')); // filter out any non-extension types

  const formatBytes = (bytes: number, decimals = 2) => {
    if (!isFinite(bytes)) return '-';
    if (bytes === 0) return '0 B';
    const k = 1024;
    const dm = Math.max(0, decimals);
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)));
    const value = parseFloat((Math.abs(bytes) / Math.pow(k, i)).toFixed(dm));
    const sign = bytes < 0 ? '-' : '';
    return `${sign}${value} ${units[i]}`;
  };

  return (
    <FileDropContainer
      filesDragStarted={dragStart}
      filesDragStopped={dragEnd}
      filesDropped={fileDrop}
      filesBeingDragged={fileBeingDragged}
    >
      <TeContainerSlim gap="xs" column alignItems="center">
        {/* Preview area: show image preview if available, always show filename if selected */}
        {previewUrl && isPreviewImage(previewUrl, selectedName) ? (
          <TeContainerSlim gap="xs" column alignItems="center">
            <img
              src={previewUrl}
              alt={selectedName ?? 'preview'}
              style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 8 }}
            />
            {selectedName && (
              <TeText size={14} weight={500} style={{ marginTop: 8 }}>
                {selectedName}
              </TeText>
            )}
          </TeContainerSlim>
        ) : selectedName ? (
          // no preview image available but we have a filename -> show it
          <TeText size={14} weight={500}>
            {selectedName}
          </TeText>
        ) : (
          // default upload prompt
          <>
            <TeIcon name="upload" size="xl" />
            <TeContainerSlim gap="xs2" column alignItems="center">
              <TeText size={16} weight={500}>
                {t('uploadOrDragDrop')}
              </TeText>
              <TeText size={12} color="text.secondary">
                {t('allowedFiles', {
                  files: allowedFileExtensions.join(', '),
                  maxSize: formatBytes(maxFileSize),
                })}
              </TeText>
            </TeContainerSlim>
          </>
        )}
      </TeContainerSlim>
      <TeContainerSlim gap="xs" alignItems="center">
        {clearable && (selectedName || previewUrl) && (
          <TeButton
            size="s"
            icon="delete"
            variant="neutral"
            teclick={handleClear}
            disabled={disabled}
          />
        )}
        <UploadButton
          text={selectedName || previewUrl ? t('replaceFile') : t('uploadNewFile')}
          icon={selectedName || previewUrl ? 'swap_horiz' : ''}
          disabled={disabled}
          onButtonClick={handleButtonFileSelect}
          accept={acceptedFiles.join(',')}
        />
      </TeContainerSlim>
    </FileDropContainer>
  );
};

export default UploadFileArea;
